import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { NotificationPriority } from 'hooks/useNotification';
import { ITEM_TYPES } from 'constants/itemType';

export function isSitrepWorkflowNotification(item: {
  sourceItemType?: string | null;
  appMessage?: string | null;
}): boolean {
  if (item.sourceItemType !== ITEM_TYPES.Sitrep) return false;
  const msg = (item.appMessage || '').toLowerCase();
  return msg.includes('review') || msg.includes('report');
}

export function priorityBadgeVariant(
  priority: string | undefined
): 'destructive' | 'amber' | 'blue' | 'green' | 'secondary' | 'outline' {
  if (!priority) return 'secondary';
  switch (priority.toLowerCase()) {
    case NotificationPriority.STAND_DOWN_SAFETY:
    case NotificationPriority.CRITICAL:
      return 'destructive';
    case NotificationPriority.WARNING:
      return 'amber';
    case NotificationPriority.WATCH:
      return 'green';
    case NotificationPriority.ADVISORY:
      return 'blue';
    default:
      return 'secondary';
  }
}

export function notificationBadgeVariant(
  priority: string | undefined,
  label?: string
): ReturnType<typeof priorityBadgeVariant> {
  if (label?.toLowerCase() === 'report') return 'blue';
  return priorityBadgeVariant(priority);
}

export function severityLabel(priority: string | undefined): string {
  if (!priority) return 'Info';
  if (priority.toLowerCase() === NotificationPriority.STAND_DOWN_SAFETY) {
    return 'Stand Down Safety';
  }
  if (priority.toLowerCase() === 'critical') return 'Critical';
  return priority.charAt(0).toUpperCase() + priority.slice(1).toLowerCase();
}

/** Hub-style header severity badge classes (Critical / Warning / …). */
export function getNotificationSeverityBadgeClasses(
  priority: string | undefined,
  label?: string
): string {
  const variant = notificationBadgeVariant(priority, label);
  return cn(
    variant === 'destructive' &&
      'tw:border-transparent tw:bg-destructive tw:text-destructive-foreground',
    variant === 'amber' &&
      'tw:border-transparent tw:bg-amber-500/25 tw:text-amber-100',
    variant === 'blue' && 'tw:border-info/40 tw:bg-info/10 tw:text-info',
    variant === 'green' &&
      'tw:border-success/40 tw:bg-success/10 tw:text-success',
    variant === 'secondary' &&
      'tw:border-transparent tw:bg-secondary tw:text-secondary-foreground'
  );
}

export function NotificationSeverityBadge({
  priority,
  label,
}: {
  priority: string | undefined;
  label?: string;
}) {
  if (!priority && !label) return null;
  return (
    <Badge
      className={cn(
        'tw:shrink-0 tw:capitalize',
        getNotificationSeverityBadgeClasses(priority, label)
      )}
    >
      {label ?? severityLabel(priority)}
    </Badge>
  );
}

export function formatNotificationTimestamp(
  value: Date | string | number | null | undefined
): string {
  if (!value) return 'Unknown time';
  const formatter = new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
    timeZoneName: 'short',
  });
  return formatter.format(new Date(value)).replace(',', '');
}

/** Severity strip for SitStat notification cards (readable in light and dark). */
export function NotificationSeverityBanner({
  priority,
  label,
}: {
  priority: string | undefined;
  label?: string;
}) {
  if (!priority && !label) return null;
  const variant = notificationBadgeVariant(priority, label);
  return (
    <div
      className={cn(
        'tw:rounded-t-[inherit] tw:border-b tw:px-3 tw:py-1.5',
        variant === 'destructive' &&
          'tw:border-destructive/30 tw:bg-destructive tw:text-destructive-foreground',
        variant === 'amber' &&
          'tw:border-amber-300 tw:bg-amber-50 tw:text-amber-800 tw:dark:border-amber-500/40 tw:dark:bg-amber-500/25 tw:dark:text-amber-100',
        variant === 'blue' && 'tw:border-info/40 tw:bg-info/10 tw:text-info',
        variant === 'green' &&
          'tw:border-success/40 tw:bg-success/10 tw:text-success',
        variant === 'secondary' &&
          'tw:border-border tw:bg-secondary tw:text-secondary-foreground'
      )}
    >
      <span className="tw:text-[11px] tw:font-semibold tw:uppercase tw:tracking-wide">
        {label ?? severityLabel(priority)}
      </span>
    </div>
  );
}

export function reviewerCommentByline(comment: {
  reviewerName?: string | null;
  seatName?: string | null;
}): string | null {
  const name = comment.reviewerName?.trim();
  const seat = comment.seatName?.trim();
  if (name && seat) return `${name} · ${seat}`;
  return name || seat || null;
}

export function ReviewerCommentsBlock({
  comments,
}: {
  comments?: Array<{
    comment: string;
    reviewerName?: string | null;
    seatName?: string | null;
  }>;
}) {
  if (!comments?.length) return null;
  return (
    <div className="tw:min-w-0 tw:space-y-2">
      <p className="tw:m-0 tw:text-sm tw:font-semibold tw:text-foreground">
        {comments.length > 1 ? 'Reviewer comments' : 'Reviewer comment'}
      </p>
      {comments.map((entry, index) => {
        const byline = reviewerCommentByline(entry);
        return (
          <div
            key={`${entry.comment}-${index}`}
            className="tw:min-w-0 tw:space-y-1 tw:rounded-md tw:border tw:border-border tw:bg-muted/40 tw:px-3 tw:py-2"
          >
            {byline ? (
              <p className="tw:m-0 tw:min-w-0 tw:break-words tw:text-xs tw:text-muted-foreground">
                {byline}
              </p>
            ) : null}
            <p className="tw:m-0 tw:min-w-0 tw:whitespace-pre-wrap tw:break-words tw:text-sm tw:text-foreground">
              {entry.comment}
            </p>
          </div>
        );
      })}
    </div>
  );
}
